import { api } from "./api-client";
import type { Locale } from "./i18n/config";
import { internalMetricsEnabled } from "./tracking/track";

// "La palabra del día": la palabra vive solo en el servidor (Render, sin
// pasar por functions de Netlify); acá llegan solo el largo y los colores
// de cada intento (sin pistas, como el Wordle clásico).

export type LetterResult = "correct" | "present" | "absent";

export interface DailyWordToday {
  puzzle: number;
  length: number;
  maxAttempts: number;
  nextChangeAt: string;
}

// Evento donde aparece la palabra (se muestra al acertar).
export interface EventLink {
  slug: string;
  title: string;
  imageUrl?: string;
  date: string;
  place?: string;
  country: string;
}

export interface DailyWordGuessResult {
  result: LetterResult[];
  solved: boolean;
  events?: EventLink[];
  // Solo al acertar: al perder el servidor no la entrega.
  answer?: string;
  // Sin sesión: comprobante firmado de la partida hasta este intento.
  token?: string;
  // Con sesión: estadísticas actualizadas.
  stats?: DailyWordStats;
}

export interface DailyWordStats {
  played: number;
  wins: number;
  winRate: number;
  currentStreak: number;
  maxStreak: number;
  // Victorias en 1, 2, …, 6 intentos.
  distribution: number[];
}

export interface DailyWordGame {
  puzzle: number;
  guesses: string[];
  results: LetterResult[][];
  status: "playing" | "won" | "lost";
  answer?: string;
  events?: EventLink[];
  // Sin sesión: comprobante para seguir la partida y para pasarla a la cuenta
  // al iniciar sesión.
  token?: string;
  // Idioma en que se juega (una partida al día, en cualquier idioma).
  lang?: Locale;
}

export function fetchDailyWordToday(lang: Locale, country: string): Promise<DailyWordToday> {
  return api.get<DailyWordToday>(`/daily-word/today?lang=${lang}&country=${country}`);
}

interface GuessBody {
  lang: Locale;
  country: string;
  puzzle: number;
  guess: string;
  token?: string;
}

// Métricas internas de la partida (cuántos inician/terminan): el server no
// cuenta los navegadores del equipo ni los de Staging.
function withMeasure<T extends object>(body: T): T & { platform: "web"; internal: boolean } {
  return { ...body, platform: "web", internal: !internalMetricsEnabled() };
}

// Primera letra escrita: parte el reloj del ranking por tiempo. Sin sesión
// devuelve el comprobante con la hora de inicio.
export function startDailyWord(body: { lang: Locale; country: string; puzzle: number; token?: string }): Promise<{
  token: string;
}> {
  return api.post<{ token: string }>("/daily-word/start", withMeasure(body));
}

export function startMyDailyWord(body: { lang: Locale; country: string; puzzle: number }): Promise<unknown> {
  return api.post<unknown>("/daily-word/me/start", withMeasure(body));
}

export function sendDailyWordGuess(body: GuessBody): Promise<DailyWordGuessResult> {
  return api.post<DailyWordGuessResult>("/daily-word/guess", withMeasure(body));
}

// ---- Con sesión: la partida y las estadísticas viven en la cuenta ----

export interface MyDailyWordToday extends DailyWordToday {
  game: DailyWordGame | null;
  stats: DailyWordStats;
  // La de hoy ya la jugó en otro idioma (no puede jugar otra).
  playedIn?: Locale;
}

export function fetchMyDailyWordToday(lang: Locale, country: string): Promise<MyDailyWordToday> {
  return api.get<MyDailyWordToday>(`/daily-word/me/today?lang=${lang}&country=${country}`);
}

export function sendMyDailyWordGuess(body: Omit<GuessBody, "token">): Promise<DailyWordGuessResult> {
  return api.post<DailyWordGuessResult>("/daily-word/me/guess", withMeasure(body));
}

// Pasa a la cuenta la partida jugada sin sesión en este navegador.
export function claimDailyWordGame(body: { lang: Locale; country: string; token: string }): Promise<{
  imported: boolean;
}> {
  return api.post<{ imported: boolean }>("/daily-word/me/claim", body);
}

// Teclados: el español suma la Ñ (es una letra propia, no una N).
export const KEYBOARD_ROWS: Record<Locale, string[][]> = {
  es: [
    ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
    ["A", "S", "D", "F", "G", "H", "J", "K", "L", "Ñ"],
    ["ENTER", "Z", "X", "C", "V", "B", "N", "M", "BACKSPACE"],
  ],
  en: [
    ["Q", "W", "E", "R", "T", "Y", "U", "I", "O", "P"],
    ["A", "S", "D", "F", "G", "H", "J", "K", "L"],
    ["ENTER", "Z", "X", "C", "V", "B", "N", "M", "BACKSPACE"],
  ],
};

// Tecla física → letra del juego ("á" → "A", "ñ" → "Ñ"); null si no es una
// letra válida para ese idioma.
export function toGameLetter(key: string, lang: Locale): string | null {
  if ([...key].length !== 1) return null;
  const upper = key.toUpperCase();
  const letter = upper === "Ñ" ? upper : upper.normalize("NFD").replace(/[̀-ͯ]/g, "");
  if (letter === "Ñ") return lang === "es" ? letter : null;
  return /^[A-Z]$/.test(letter) ? letter : null;
}

// Fila que se está escribiendo: una casilla por letra ("" = vacía) y la
// casilla activa (`letters.length` = ninguna: la fila se llenó escribiendo).
export interface WordDraft {
  letters: string[];
  cursor: number;
}

export function emptyDraft(length: number): WordDraft {
  return { letters: Array.from({ length }, () => ""), cursor: 0 };
}

export function draftComplete(draft: WordDraft): boolean {
  return draft.letters.every(Boolean);
}

// Escribe en la casilla activa y pasa a la siguiente vacía a su derecha.
export function typeInDraft(draft: WordDraft, letter: string): WordDraft {
  const { letters, cursor } = draft;
  const target = cursor < letters.length ? cursor : letters.findIndex((l) => !l);
  if (target < 0) return draft;
  const next = letters.map((l, i) => (i === target ? letter : l));
  const empty = next.findIndex((l, i) => i > target && !l);
  return { letters: next, cursor: empty >= 0 ? empty : target + 1 };
}

// Borra la casilla activa; si está vacía, la anterior.
export function eraseInDraft(draft: WordDraft): WordDraft {
  const { letters, cursor } = draft;
  const target = letters[cursor] ? cursor : cursor - 1;
  if (target < 0) return draft;
  return { letters: letters.map((l, i) => (i === target ? "" : l)), cursor: target };
}

// Mejor estado conocido de cada letra, para pintar el teclado.
export function keyStates(game: DailyWordGame): Map<string, LetterResult> {
  const rank: Record<LetterResult, number> = { absent: 0, present: 1, correct: 2 };
  const states = new Map<string, LetterResult>();
  game.guesses.forEach((guess, row) => {
    [...guess].forEach((letter, i) => {
      const state = game.results[row]?.[i];
      if (!state) return;
      const prev = states.get(letter);
      if (!prev || rank[state] > rank[prev]) states.set(letter, state);
    });
  });
  return states;
}

// Cuadritos para compartir, sin revelar letras.
export function resultGrid(results: LetterResult[][]): string {
  const square: Record<LetterResult, string> = { correct: "🟩", present: "🟨", absent: "⬜" };
  return results.map((row) => row.map((r) => square[r]).join("")).join("\n");
}

// Progreso sin cuenta: solo en este navegador, una partida por idioma (la
// de otro día se descarta al cargar).
// Una sola partida guardada, en cualquier idioma: cambiar de idioma no da
// otra (Jonathan 2026-10-25).
const STORAGE_KEY = "cv_daily_word";

// Partida sin sesión guardada (de cualquier día e idioma).
export function loadAnyDailyWordGame(): DailyWordGame | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const game = raw ? (JSON.parse(raw) as DailyWordGame) : null;
    return game && Array.isArray(game.guesses) ? game : null;
  } catch {
    return null;
  }
}

// La partida de hoy en este idioma, o el idioma en que ya se jugó hoy.
export function loadDailyWordGame(
  lang: Locale,
  puzzle: number,
): { game: DailyWordGame | null; playedIn?: Locale } {
  const game = loadAnyDailyWordGame();
  if (!game || game.puzzle !== puzzle) return { game: null };
  if (game.lang && game.lang !== lang) {
    // Solo cuenta si alcanzó a mandar un intento.
    return game.guesses.length > 0 ? { game: null, playedIn: game.lang } : { game: null };
  }
  return { game };
}

export function clearDailyWordGame(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage bloqueado: no hay nada guardado que borrar.
  }
}

export function saveDailyWordGame(lang: Locale, game: DailyWordGame): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...game, lang }));
  } catch {
    // Modo privado / storage bloqueado: la partida dura solo esta visita.
  }
}

// ---- Ranking (opt-in) y perfil de jugador ----

export type RankingScope = "global" | "country";
export type RankingPeriod = "week" | "month";

export interface PlayerCard {
  name: string;
  // null → avatar generado desde avatarSeed.
  avatarUrl: string | null;
  avatarSeed: string;
}

export interface RankingEntry extends PlayerCard {
  position: number;
  country?: string;
  points: number;
  wins: number;
  streak: number;
}

export interface RankingResult {
  period: RankingPeriod;
  scope: RankingScope;
  total: number;
  entries: RankingEntry[];
  // Con sesión: la fila propia (joined false = solo la ve esa persona).
  me: (RankingEntry & { joined: boolean; topPercent: number }) | null;
}

// Rankings por tiempo (palabra del día y sopa de letras): solo partidas
// terminadas. Promedio de todos sus tiempos o su mejor tiempo histórico.
export type TimeRankingMode = "average" | "best";
export type TimeRankingPeriod = "all" | "week" | "month";

export interface TimeRankingEntry extends PlayerCard {
  position: number;
  country?: string;
  averageMs: number;
  bestMs: number;
  completed: number;
}

export interface TimeRankingResult {
  mode: TimeRankingMode;
  period: TimeRankingPeriod;
  scope: RankingScope;
  total: number;
  entries: TimeRankingEntry[];
  me: (TimeRankingEntry & { joined: boolean; topPercent: number }) | null;
}

export function fetchDailyWordTimeRanking(
  params: { lang: Locale; country: string; scope: RankingScope; mode: TimeRankingMode; period: TimeRankingPeriod },
  loggedIn: boolean,
): Promise<TimeRankingResult> {
  const query = new URLSearchParams(params).toString();
  return api.get<TimeRankingResult>(`/daily-word/${loggedIn ? "me/" : ""}ranking/time?${query}`);
}

export interface PlayerProfile {
  joined: boolean;
  alias: string | null;
  showPhoto: boolean;
  // Su propia foto de perfil (para la vista previa), aunque esté oculta.
  photoUrl: string | null;
  firstName: string;
  promptAnswered: boolean;
  card: PlayerCard;
}

export function fetchDailyWordRanking(
  params: { lang: Locale; country: string; scope: RankingScope; period: RankingPeriod },
  loggedIn: boolean,
): Promise<RankingResult> {
  const query = new URLSearchParams(params).toString();
  return api.get<RankingResult>(`/daily-word/${loggedIn ? "me/" : ""}ranking?${query}`);
}

export function fetchPlayerProfile(): Promise<PlayerProfile> {
  return api.get<PlayerProfile>("/daily-word/me/profile");
}

export function updatePlayerProfile(body: {
  joined?: boolean;
  alias?: string | null;
  showPhoto?: boolean;
  promptAnswered?: boolean;
}): Promise<PlayerProfile> {
  return api.patch<PlayerProfile>("/daily-word/me/profile", body);
}

// ---- Admin: palabras de los próximos días ----

export type WordSource = "gemini" | "frequency" | "curated" | "manual";

export interface AdminDailyWord {
  lang: Locale;
  // Cada país juega con palabras de sus eventos.
  country: string;
  puzzle: number;
  date: string;
  word: string;
  // null: sin generar (se jugaría la de la lista curada).
  source: WordSource | null;
  // true: sin palabra propia ese día; juega la de Chile (o EE.UU. en inglés).
  fallback: boolean;
  // false: la partida ya empezó en algún país.
  editable: boolean;
  events: { slug: string; title: string }[];
}

export function fetchAdminDailyWords(): Promise<AdminDailyWord[]> {
  return api.get<AdminDailyWord[]>("/admin/daily-word/words");
}

export function setAdminDailyWord(
  lang: Locale,
  country: string,
  puzzle: number,
  word: string,
): Promise<{ inDictionary: boolean }> {
  return api.patch<{ inDictionary: boolean }>(`/admin/daily-word/words/${lang}/${country}/${puzzle}`, { word });
}

export function generateAdminDailyWords(notify: boolean): Promise<unknown> {
  return api.post<unknown>("/admin/daily-word/generate", { notify });
}
